import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ApiError, createGroup, deleteGroup, updateGroup } from "@/lib/api";
import { queryKeys, useGroups } from "@/lib/queries";

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function GroupsDialog({ trigger }: { trigger: ReactNode }) {
  const queryClient = useQueryClient();
  const groupsQuery = useGroups();
  const [names, setNames] = useState(() => new Map<number, string>());
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (!groupsQuery.data) return;
    setNames(current => {
      const next = new Map<number, string>();
      for (const group of groupsQuery.data) next.set(group.id, current.get(group.id) ?? group.name);
      return next;
    });
  }, [groupsQuery.data]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.groups }),
      queryClient.invalidateQueries({ queryKey: queryKeys.monitors }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: createGroup,
    onSuccess: async () => {
      setNewName("");
      toast.success("Group created");
      await refresh();
    },
    onError: error => toast.error(errorMessage(error, "Could not create group")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => updateGroup(id, name),
    onSuccess: async () => {
      toast.success("Group renamed");
      await refresh();
    },
    onError: error => toast.error(errorMessage(error, "Could not rename group")),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteGroup,
    onSuccess: async () => {
      toast.success("Group deleted");
      await refresh();
    },
    onError: error => toast.error(errorMessage(error, "Could not delete group")),
  });

  const saveName = (id: number, originalName: string) => {
    const name = (names.get(id) ?? "").trim();
    if (!name) {
      setNames(current => new Map(current).set(id, originalName));
      return;
    }
    if (name !== originalName && !updateMutation.isPending) updateMutation.mutate({ id, name });
  };

  const addGroup = () => {
    const name = newName.trim();
    if (name && !createMutation.isPending) createMutation.mutate(name);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage groups</DialogTitle>
          <DialogDescription>Organize monitors without changing how they run.</DialogDescription>
        </DialogHeader>

        <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
          {groupsQuery.isLoading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          )}
          {groupsQuery.data?.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">No groups yet.</p>
          )}
          {groupsQuery.data?.map(group => (
            <div key={group.id} className="flex items-center gap-2 rounded-lg border p-2">
              <Input
                value={names.get(group.id) ?? group.name}
                aria-label={`Rename ${group.name}`}
                onChange={event => setNames(current => new Map(current).set(group.id, event.target.value))}
                onBlur={() => saveName(group.id, group.name)}
                onKeyDown={event => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  if (event.key === "Escape") {
                    setNames(current => new Map(current).set(group.id, group.name));
                    event.currentTarget.blur();
                  }
                }}
              />
              <span className="w-20 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                {group.monitorCount} {group.monitorCount === 1 ? "monitor" : "monitors"}
              </span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" aria-label={`Delete ${group.name}`}>
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete “{group.name}”?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Monitors keep running; they just lose the group.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      variant="destructive"
                      onClick={() => deleteMutation.mutate(group.id)}
                    >
                      Delete group
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>

        <div className="flex gap-2 border-t pt-4">
          <Input
            value={newName}
            placeholder="New group name"
            aria-label="New group name"
            onChange={event => setNewName(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") addGroup();
            }}
          />
          <Button type="button" onClick={addGroup} disabled={!newName.trim() || createMutation.isPending}>
            {createMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
